import * as React from "react";

import { ValidationState } from "modules/claims/claimSubmission/models";
import { FormDropdownField, FormTextField, MaintenanceLockSubmitButton, SelectOption } from "modules/common/components/forms";
import { Address, Country } from "../models";
import { nameof } from "modules/utils";
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { ConfirmUnsavedChanges } from "modules/common/components/forms/confirmUnsavedChanges";
import { MonthEndMaintenanceAlert } from "modules/maintenance/components";
import { useState } from "react";
import { useAppSelector } from "hooks/reduxHooks";
import FeatureToggleChecker from "modules/utils/featureToggleChecker";

export interface AddressFormProps {
    profileAddress: Address;
    countries: Country[];
    isSaving: boolean;
    onSaveAddressRequested: (address: Address) => any;
    onCancel: () => any;
    setProvinceAndCountryAsReadOnly?: boolean;
}

export interface AddressFormState {
    formAddress: Address;
    hasAttemptedToSubmit: boolean;
    validationState: ValidationState;
    showCancelConfirmation: boolean;
    isFormDirty: boolean;
}

// Any longer than 30 and it will get truncated in the ABC Compare file.
// Any longer than 40 characters and it can cause problems for the DailyCRM.
const maxFieldLength = 30;

export const AddressForm = (props: AddressFormProps) => {
    const initState = {
        formAddress: props.profileAddress,
        hasAttemptedToSubmit: false,
        validationState: { isValid: true, messages: {} },
        showCancelConfirmation: false,
        isFormDirty: false
    };
    const [formState, setFormState] = useState<AddressFormState>(initState);
    const hideSuiteNumberToggleFeature = useAppSelector((state) => state.features.featureToggles.HideSuiteNumber);
    const showSuiteNumber = !FeatureToggleChecker.isOn(hideSuiteNumberToggleFeature);

    const getProvincesList = (country: string): SelectOption[] => {
        if (country && props.countries.filter((c) => c.fullName === country).length) {
            const selectedCountry = props.countries.filter((c) => c.fullName === formState.formAddress.country)[0];
            return selectedCountry.provinces.map(
                (province) =>
                    ({
                        value: province.fullName,
                        text: province.fullName
                    } as SelectOption)
            );
        }
        return [];
    };

    const getCountriesList = (countries: Country[]): SelectOption[] => {
        return (countries || []).map(
            (country) =>
                ({
                    value: country.fullName,
                    text: country.fullName
                } as SelectOption)
        );
    };

    const onSuiteNumberChanged = (event: any) => {
        setAddressFieldValue(nameof<Address>("suiteNumber"), event.target.value);
    };

    const onStreetChanged = (event: any) => {
        setAddressFieldValue(nameof<Address>("street"), event.target.value);
    };

    const onCityChanged = (event: any) => {
        setAddressFieldValue(nameof<Address>("city"), event.target.value);
    };

    const onPostalCodeChanged = (event: any) => {
        setAddressFieldValue(nameof<Address>("postalCode"), event.target.value);
    };

    const onCountryChanged = (event: any) => {
        setAddressValue({
            ...formState.formAddress,
            country: event.target.value,
            province: "0"
        } as Address);
    };

    const onProvinceChanged = (event: any) => {
        setAddressFieldValue(nameof<Address>("province"), event.target.value);
    };

    const setAddressFieldValue = (fieldName: string, value: any) => {
        const updatedAddress = {
            ...formState.formAddress,
            [fieldName]: value
        };
        setFormState((s) => ({ ...s, formAddress: updatedAddress, isFormDirty: true }));
        validate(updatedAddress);
    };

    const setAddressValue = (address: Address) => {
        setFormState((s) => ({ ...s, formAddress: address, isFormDirty: true }));
        validate(address);
    };

    const validate = (address: Address) => {
        const countryCanada = "Canada";
        const countryUnitedStates = "United States";

        const validationResult = { isValid: true, messages: {} } as ValidationState;
        const requiredFieldMissingMessage = "This field is required";

        if (address) {
            if (!address.street) {
                validationResult.messages[nameof<Address>("street")] = requiredFieldMissingMessage;
            }
            if (!address.city) {
                validationResult.messages[nameof<Address>("city")] = requiredFieldMissingMessage;
            }
            if (!address.postalCode) {
                validationResult.messages[nameof<Address>("postalCode")] = requiredFieldMissingMessage;
            } else {
                const isValidZip = /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(address.postalCode);
                const isValidPostalCode = /^([A-Z][0-9][A-Z])\s*([0-9][A-Z][0-9])$/i.test(address.postalCode);

                if (address.country === countryCanada && !isValidPostalCode) {
                    validationResult.messages[nameof<Address>("postalCode")] = "Use valid format (e.g. A1A 1A1)";
                }

                if (address.country === countryUnitedStates && !isValidZip) {
                    validationResult.messages[nameof<Address>("postalCode")] = "Use valid format (e.g. 12345)";
                }
            }

            if (!address.country) {
                validationResult.messages[nameof<Address>("country")] = requiredFieldMissingMessage;
            }

            if (!address.province) {
                validationResult.messages[nameof<Address>("province")] = requiredFieldMissingMessage;
            }

            validationResult.isValid = Object.keys(validationResult.messages).length === 0;
        } else {
            validationResult.isValid = false;
        }

        setFormState((s) => ({ ...s, validationState: validationResult }));
    };

    const onSaveAddressRequested = () => {
        setFormState((s) => ({ ...s, hasAttemptedToSubmit: true }));

        validate(formState.formAddress);

        if (formState.validationState.isValid) {
            props.onSaveAddressRequested(formState.formAddress);
        }
    };
    const onCancel = () => {
        if (formState.isFormDirty) {
            setFormState((s) => ({ ...s, showCancelConfirmation: true }));
        } else {
            cancelConfirmed();
        }
    };

    const cancelConfirmed = () => {
        setFormState(initState);
        props.onCancel();
    };

    const cancelCanceled = () => {
        setFormState((s) => ({ ...s, showCancelConfirmation: false }));
    };

    const hasStreetError =
        formState.hasAttemptedToSubmit && !!formState.validationState.messages[nameof<Address>("street")];
    const hasCityError =
        formState.hasAttemptedToSubmit && !!formState.validationState.messages[nameof<Address>("city")];
    const hasCountryError =
        formState.hasAttemptedToSubmit && !!formState.validationState.messages[nameof<Address>("country")];
    const hasProvinceError =
        formState.hasAttemptedToSubmit && !!formState.validationState.messages[nameof<Address>("province")];
    const hasPostalCodeError =
        formState.hasAttemptedToSubmit && !!formState.validationState.messages[nameof<Address>("postalCode")];

    return (
        <>
            <Grid container>
                <Grid item xs={12} md={3} />
                <Grid item xs={12} md={9}>
                    <Grid container>
                        <Grid item xs={12} sm={8} className="profile-form">
                            <Grid container spacing={2}>
                                <Grid item xs={12} className="month-end-maintenance-alert-item">
                                    <MonthEndMaintenanceAlert />
                                </Grid>
                                <Grid item xs={12} sm={showSuiteNumber ? 8 : 12}>
                                    <FormTextField
                                        title="Street address"
                                        value={formState.formAddress.street}
                                        onChanged={onStreetChanged}
                                        maxLength={maxFieldLength}
                                        errorText={
                                            hasStreetError
                                                ? formState.validationState.messages[nameof<Address>("street")]
                                                : null
                                        }
                                        disabled={props.isSaving}
                                        fullWidth
                                        id="streetAddress"
                                        className="profile-form-control"
                                    />
                                </Grid>
                                {showSuiteNumber && (
                                    <Grid item xs={12} sm={4}>
                                        <FormTextField
                                            title="Apartment, suite or unit"
                                            value={
                                                formState.formAddress.suiteNumber
                                                    ? formState.formAddress.suiteNumber
                                                    : ""
                                            }
                                            placeholder="(optional)"
                                            onChanged={onSuiteNumberChanged}
                                            disabled={props.isSaving}
                                            fullWidth
                                            id="suiteNumber"
                                            className="profile-form-control"
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={8}>
                                    <FormTextField
                                        title="City"
                                        value={formState.formAddress.city}
                                        onChanged={onCityChanged}
                                        maxLength={maxFieldLength}
                                        errorText={
                                            hasCityError
                                                ? formState.validationState.messages[nameof<Address>("city")]
                                                : null
                                        }
                                        disabled={props.isSaving}
                                        fullWidth
                                        id="city"
                                        className="profile-form-control"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormDropdownField
                                        title="Country"
                                        options={getCountriesList(props.countries)}
                                        value={formState.formAddress.country}
                                        onChanged={onCountryChanged}
                                        errorText={
                                            hasCountryError
                                                ? formState.validationState.messages[nameof<Address>("country")]
                                                : null
                                        }
                                        className="profile-form-control"
                                        disabled={props.isSaving || props.setProvinceAndCountryAsReadOnly}
                                        fullWidth
                                        id="country"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <FormDropdownField
                                        title="Province/State"
                                        disabled={
                                            !formState.formAddress.country ||
                                            props.isSaving ||
                                            props.setProvinceAndCountryAsReadOnly
                                        }
                                        options={getProvincesList(formState.formAddress.country)}
                                        value={formState.formAddress.province}
                                        onChanged={onProvinceChanged}
                                        errorText={
                                            hasProvinceError
                                                ? formState.validationState.messages[nameof<Address>("province")]
                                                : null
                                        }
                                        className="profile-form-control"
                                        fullWidth
                                        id="province"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormTextField
                                        title={"Postal/ZIP Code"}
                                        value={formState.formAddress.postalCode}
                                        onChanged={onPostalCodeChanged}
                                        errorText={
                                            hasPostalCodeError
                                                ? formState.validationState.messages[nameof<Address>("postalCode")]
                                                : null
                                        }
                                        disabled={props.isSaving}
                                        fullWidth
                                        id="postalCode"
                                        className="profile-form-control"
                                    />
                                </Grid>
                                <Grid item xs={12} className="form-actions gutter-top">
                                    <MaintenanceLockSubmitButton
                                        submittingMessage="Updating"
                                        isSubmitting={props.isSaving}
                                        className="secondary"
                                        variant="contained"
                                        color="primary"
                                        onClick={onSaveAddressRequested}
                                        disabled={!formState.isFormDirty}>
                                        Update Address
                                    </MaintenanceLockSubmitButton>
                                    <Button disabled={props.isSaving} onClick={onCancel}>
                                        Cancel
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Dialog open={formState.showCancelConfirmation} onClose={cancelCanceled}>
                <DialogTitle>Are you sure?</DialogTitle>
                <DialogContent>Any changes you made will be lost.</DialogContent>
                <DialogActions>
                    <Button onClick={cancelCanceled}>No, Keep It</Button>
                    <Button className="confirmation confirm-btn" onClick={cancelConfirmed}>
                        Yes, discard my changes
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmUnsavedChanges isDirty={formState.isFormDirty} />
        </>
    );
};
